import { useCallback } from 'react';
import type { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CloseFullscreen,
  OpenInFull,
  People,
  TrendingDown,
  TrendingUp,
} from '@mui/icons-material';
import { Button, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material';
import { FlapDisplay, Presets } from 'split-flap-lib';
import 'split-flap-lib/dist/index.css';

import { BackButton } from '../components/back-button';
import { useSalesScoreboardQuery } from '../store/hooks';
import { formatCents, formatRelativeTime } from '../utils/formatting';

const GOLIVE_GOAL = 50;
const FLAP_CHARACTERS = `${Presets.ALPHANUM}$%,./:-+`;

function sanitizeFlapValue(value: string, length: number): string {
  const sanitized = value
    .toUpperCase()
    .split('')
    .map(char => (FLAP_CHARACTERS.includes(char) ? char : ' '))
    .join('');
  return sanitized.slice(0, length);
}

function FlipBoardValue({ value, length }: { value: string; length: number }): ReactElement {
  return (
    <FlapDisplay
      chars={FLAP_CHARACTERS}
      className="rcm-split-flap-display"
      hinge
      length={length}
      padMode="end"
      timing={18}
      value={sanitizeFlapValue(value, length)}
    />
  );
}

function FlipMetric({
  label,
  value,
  length = 8,
  caption,
  fullscreen = false,
}: {
  label: string;
  value: string;
  length?: number;
  caption?: string;
  fullscreen?: boolean;
}): ReactElement {
  return (
    <div
      className={
        fullscreen
          ? 'flex min-h-[min(42vh,440px)] flex-col justify-center rounded-md border border-zinc-700 bg-zinc-950 p-7 shadow-sm'
          : 'min-h-[300px] rounded-md border border-zinc-700 bg-zinc-950 p-6 shadow-sm'
      }
    >
      <div
        className={
          fullscreen
            ? 'mb-5 text-base font-semibold uppercase tracking-wide text-zinc-400'
            : 'mb-4 text-sm font-semibold uppercase tracking-wide text-zinc-400'
        }
      >
        {label}
      </div>
      <div className="overflow-hidden">
        <FlipBoardValue value={value} length={length} />
      </div>
      {caption && (
        <div className={fullscreen ? 'mt-5 text-base text-zinc-500' : 'mt-4 text-sm text-zinc-500'}>
          {caption}
        </div>
      )}
    </div>
  );
}

function StatPill({
  icon,
  label,
  value,
  tone,
}: {
  icon: ReactElement;
  label: string;
  value: string;
  tone: string;
}): ReactElement {
  return (
    <div className="rounded-md border border-gray-200 bg-white px-5 py-5">
      <div className="flex items-center gap-3 text-sm font-medium uppercase text-gray-500">
        <span className={`flex h-9 w-9 items-center justify-center rounded-md ${tone}`}>
          {icon}
        </span>
        {label}
      </div>
      <div className="mt-4 text-3xl font-semibold text-gray-950">{value}</div>
    </div>
  );
}

function formatFlapMoney(cents: number | undefined | null): string {
  const dollars = Math.round((cents ?? 0) / 100);
  if (Math.abs(dollars) >= 1000) {
    return `$${(dollars / 1000).toFixed(1)}K`;
  }
  const sign = dollars < 0 ? '-' : '+';
  return `${sign}$${Math.abs(dollars).toLocaleString('en-US')}`;
}

function formatFlapPct(value: number | undefined | null): string {
  if (value == null) return '0.0%';
  return `${value.toFixed(1)}%`;
}

function formatFlapCount(value: number | undefined | null): string {
  return String(value ?? 0);
}

export default function SalesScoreboard(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const isFullscreenMode = searchParams.get('fullscreen') === '1';

  const { data, error, refetch } = useSalesScoreboardQuery(
    {},
    { pollingInterval: 5 * 60 * 1000 },
  );

  const isCacheWarming = data != null && data.computed_at == null;

  const golivePct = Math.min(data?.jotbill_golive_pct ?? 0, 100);

  const updateParams = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
      }
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const enterFullscreenMode = useCallback(() => {
    updateParams({ fullscreen: '1' });
    const fullscreenRequest = document.documentElement.requestFullscreen?.();
    void fullscreenRequest?.catch(() => undefined);
  }, [updateParams]);

  const exitFullscreenMode = useCallback(() => {
    updateParams({ fullscreen: undefined });
    if (document.fullscreenElement) {
      const exitRequest = document.exitFullscreen?.();
      void exitRequest?.catch(() => undefined);
    }
  }, [updateParams]);

  const renderTickerGrid = (fullscreen = false): ReactElement => (
    <div
      className={
        fullscreen
          ? 'rcm-scoreboard-fullscreen grid w-full grid-cols-1 gap-5 lg:grid-cols-2'
          : 'mb-6 grid grid-cols-1 gap-5 rounded-md bg-zinc-900 p-5 shadow-sm lg:grid-cols-2'
      }
    >
      {isCacheWarming ? (
        <div className={`col-span-full flex items-center justify-center ${fullscreen ? 'min-h-[min(42vh,440px)]' : 'min-h-[300px]'} rounded-md border border-zinc-700 bg-zinc-950 p-6`}>
          <Typography variant="h5" className="animate-pulse text-zinc-400">
            Updating…
          </Typography>
        </div>
      ) : (
        <>
          <FlipMetric
            label="Net MRR growth"
            value={formatFlapMoney(data?.net_mrr_growth_cents)}
            caption={`Current MRR ${formatCents(data?.mrr_current_cents)}`}
            fullscreen={fullscreen}
          />
          <FlipMetric
            label="30-day churn"
            value={formatFlapPct(data?.gross_churn_pct)}
            caption={`${String(data?.churned_logos ?? 0)} churned of ${String(data?.churn_base_logos ?? 0)} logos`}
            fullscreen={fullscreen}
          />
          <FlipMetric
            label="JotBill Go-live"
            value={formatFlapCount(data?.jotbill_golive_count)}
            length={6}
            caption={`Goal: ${String(data?.jotbill_golive_goal ?? GOLIVE_GOAL)} by June 30`}
            fullscreen={fullscreen}
          />
          <FlipMetric
            label="% of goal"
            value={formatFlapPct(data?.jotbill_golive_pct)}
            length={6}
            caption={`${String(data?.jotbill_golive_count ?? 0)} / ${String(data?.jotbill_golive_goal ?? GOLIVE_GOAL)}`}
            fullscreen={fullscreen}
          />
        </>
      )}
    </div>
  );

  if (isFullscreenMode) {
    return (
      <div className="fixed inset-0 z-[2000] bg-zinc-950">
        <main className="relative flex h-screen items-center overflow-hidden px-6 py-6">
          <Tooltip title="Exit ticker mode">
            <IconButton
              aria-label="Exit ticker mode"
              className="!absolute right-5 top-5 !z-10 !border !border-zinc-700 !bg-zinc-900/85 !text-zinc-200 hover:!bg-zinc-800"
              onClick={exitFullscreenMode}
              size="large"
            >
              <CloseFullscreen />
            </IconButton>
          </Tooltip>
          {renderTickerGrid(true)}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="mx-auto max-w-screen-2xl px-6 py-8">
        <BackButton fallbackUrl="/admin-v2" />

        <div className="mt-4 mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <TrendingUp className="text-zinc-800" fontSize="large" />
              <Typography variant="h4" className="font-bold text-gray-950">
                Sales Scoreboard
              </Typography>
            </div>
            <Typography variant="body1" className="mt-1 max-w-4xl text-gray-500">
              Paid MRR growth, 30-day churn, JotBill go-live count, and June target progress.
              {data?.computed_at && (
                <span className="ml-2 text-xs text-gray-400">
                  As of {formatRelativeTime(data.computed_at)}
                </span>
              )}
            </Typography>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button startIcon={<OpenInFull />} variant="outlined" onClick={enterFullscreenMode}>
              Ticker mode
            </Button>
            <Button variant="outlined" onClick={() => refetch()}>
              Refresh board
            </Button>
          </div>
        </div>

        {renderTickerGrid()}

        {!isCacheWarming && (
          <div className="mb-6 rounded-md border border-gray-200 bg-white p-6">
            <div className="mb-5">
              <div className="text-sm font-semibold uppercase text-gray-500">
                JotBill Go-live — June target
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-4xl font-bold text-gray-950">
                  {String(data?.jotbill_golive_count ?? 0)}
                </span>
                <span className="text-base text-gray-500">
                  of {String(data?.jotbill_golive_goal ?? GOLIVE_GOAL)} clinicians
                </span>
              </div>
            </div>
            <LinearProgress
              aria-label="Go-live progress"
              variant="determinate"
              value={golivePct}
              sx={{ height: 16, borderRadius: 1 }}
            />
            <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
              <span>{data?.period_start ?? ''} to {data?.period_end ?? ''}</span>
              <span>{(data?.jotbill_golive_pct ?? 0).toFixed(1)}%</span>
            </div>
          </div>
        )}

        {isCacheWarming ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Scoreboard data is updating. Metrics will appear once the cache is ready.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <StatPill
              icon={<TrendingUp fontSize="small" />}
              label="Current MRR"
              value={formatCents(data?.mrr_current_cents)}
              tone="bg-emerald-50 text-emerald-700"
            />
            <StatPill
              icon={<TrendingUp fontSize="small" />}
              label="MRR at month start"
              value={formatCents(data?.mrr_period_start_cents)}
              tone="bg-blue-50 text-blue-700"
            />
            <StatPill
              icon={<TrendingDown fontSize="small" />}
              label="Churned logos (30d)"
              value={String(data?.churned_logos ?? 0)}
              tone="bg-rose-50 text-rose-700"
            />
            <StatPill
              icon={<People fontSize="small" />}
              label="Churn base logos"
              value={String(data?.churn_base_logos ?? 0)}
              tone="bg-slate-100 text-slate-700"
            />
          </div>
        )}

        {!data?.notion_accessible && (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            JotBill Tracker Notion data is unavailable. The Notion database may not be shared with
            the integration.
            {data?.notion_error && (
              <span className="ml-1 text-amber-700">({data.notion_error})</span>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Sales scoreboard data is not available. Check that the API endpoint is deployed and
            reachable.
          </div>
        )}
      </main>
    </div>
  );
}
